import clsx from 'clsx';
import { Activity, useState, useEffect } from 'react';

import {
  IconEye,
  IconPlus,
  IconTrash,
  IconUsers,
  IconUserX,
  IconXboxX,
  IconSearch,
  IconLockOpen,
  IconUserCheck,
  IconAdjustments,
  IconLayersLinked,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import Input from '@/components/Input/Input';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table/index';
import { CommonStatusConfig } from '@/constants/constant';
import { ECommonStatus, ERestoreEntityType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getAdminList, getAdminSummary } from '@/service/adminServices';
import {
  softdeleteRecord,
  blockUnblockRecordAPI,
} from '@/service/commonService';
import useCommonStore from '@/store/useCommonStore';
import {
  IAdminData,
  IAdminSummaryData,
  TAdminSummaryItem,
  IAdminFilterValues,
  ISelectedAdminRowValue,
} from '@/types';
import {
  pickBy,
  getAssetUrl,
  customPredicate,
  formatLastActive,
} from '@/utils';

import AddAdminModal from './modal/AddAdminModal';
import AdminDetailModal from './modal/AdminDetailModal';
import AdminFilterModal from './modal/AdminFilterModal';

type TAdminListParams = {
  search?: string;
  status?: ECommonStatus;
  offset?: number;
  limit?: number;
  order?: Record<string, string>;
  [key: `role_id[${number}]`]: string;
};

const ADMIN_SUMMARY_CONFIG = [
  {
    key: 'total_admins',
    icon: <IconUsers color="#717784" />,
    label: 'user-management.admin.total-admin-ac',
  },
  {
    key: 'active_admins',
    icon: <IconUserCheck color="#717784" />,
    label: 'user-management.admin.active-admin-ac',
  },
  {
    key: 'inactive_admins',
    icon: <IconUserX color="#717784" />,
    label: 'user-management.admin.inactive-admin',
  },
];

const Admin = () => {
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);
  const [isAdminDetailsOpen, setIsAdminDetailsOpen] = useState(false);
  const [isRemoveAdminModal, setIsRemoveAdminModal] = useState(false);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<IAdminFilterValues>({
    roles: [],
    status: undefined,
  });
  const [isAddEditAdminModalOpen, setIsAddEditAdminModalOpen] = useState(false);
  const {
    loadingTable,
    setLoadingTable,
    loadingSummary,
    setLoadingSummary,
    loadingModal,
    setLoadingModal,
  } = useCommonStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [admins, setAdmins] = useState<IAdminData[]>([]);
  const [statsData, setStatsData] = useState<TAdminSummaryItem[]>(
    ADMIN_SUMMARY_CONFIG.map((item) => ({
      ...item,
      value: '0',
    }))
  );
  const [selectedAdmin, setSelectedAdmin] =
    useState<ISelectedAdminRowValue | null>(null);

  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('');
  const { formatText } = useFormatText();
  const pageTitle = formatText('user-management.admin.admin');
  const userManagement = formatText('user-management.title');

  const fetchAdminSummary = async () => {
    setLoadingSummary(true);
    try {
      const response = await getAdminSummary();
      if (response?.status && response?.data) {
        const summaryData = response.data;
        const updatedStats: TAdminSummaryItem[] = ADMIN_SUMMARY_CONFIG.map(
          (item) => {
            const key = item.key as keyof IAdminSummaryData;
            const value = summaryData[key];
            return {
              ...item,
              value: value?.toString() || '0',
            };
          }
        );
        setStatsData(updatedStats);
      }
    } catch (error) {
      console.error('Error fetching admin summary:', error);
    }
    setLoadingSummary(false);
  };

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: userManagement }, { label: pageTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle, userManagement]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    sortColumn,
    sortDirection,
    filterValues,
  ]);

  useEffect(() => {
    fetchAdminSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC');
      } else if (sortDirection === 'DESC') {
        setSortColumn('');
        setSortDirection('');
      } else {
        setSortDirection('ASC');
      }
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
  };

  const columns = [
    {
      title: 'user-management.admin.column.admin-name',
      key: 'username',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('username'),
      render: (row: IAdminData) => (
        <div className="flex items-center gap-2">
          <AvatarGroupList
            list={[
              {
                id: row?.id,
                name: row?.username,
                image: getAssetUrl(row?.asset) || row?.user_img,
              },
            ]}
            size={40}
            avatarClassName="w-10 h-10 text-xs"
          />
          <span className="font-medium text-neutral-primary">
            {row?.username || '-'}
          </span>
        </div>
      ),
    },
    {
      title: 'user-management.admin.column.email',
      key: 'email',
      width: '15%',
      sorter: true,
      handleSort: () => handleSort('email'),
    },
    {
      title: 'user-management.admin.column.role',
      key: 'role',
      width: '15%',
      sorter: true,
      render: (row: IAdminData) => row?.role_details?.name,
    },
    {
      title: 'user-management.admin.column.status',
      key: 'status',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: IAdminData) => {
        const config =
          CommonStatusConfig[row?.status as keyof typeof CommonStatusConfig];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        );
      },
    },
    {
      title: 'user-management.admin.column.last-active',
      key: 'last_active',
      width: '20%',
      sorter: true,
      handleSort: () => handleSort('last_active'),
      render: (row: IAdminData) =>
        formatLastActive(Number(row?.last_active), formatText),
    },
    {
      title: 'common.actions.action',
      key: 'action',
      width: '5%',
      align: 'center',
      render: (row: IAdminData) => {
        const isBlocked = row.status === ECommonStatus.IN_ACTIVE;
        const admin: ISelectedAdminRowValue = {
          name: row.username,
          id: row.id,
          status: row.status,
        };

        const actions = [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedAdmin(admin);
              setIsAdminDetailsOpen(true);
            },
          },
          {
            key: isBlocked ? 'unblock' : 'block',
            label: isBlocked
              ? 'common.actions.unblock'
              : 'common.actions.block',
            icon: isBlocked ? (
              <IconLockOpen size={18} />
            ) : (
              <IconXboxX size={18} />
            ),
            onClick: () => {
              if (isBlocked) {
                handleBlockAdmin(admin);
              } else {
                setSelectedAdmin(admin);
                setIsOpenBlockModal(true);
              }
            },
          },
          {
            key: 'remove',
            label: 'common.actions.remove',
            icon: <IconTrash size={18} />,
            isDanger: true,
            onClick: () => {
              setSelectedAdmin(admin);
              setIsRemoveAdminModal(true);
            },
          },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const fetchAdmins = async () => {
    setLoadingTable(true);

    let params: TAdminListParams = {
      search: debouncedSearch || undefined,
      status: filterValues?.status as ECommonStatus | undefined,
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      order:
        sortColumn && sortDirection
          ? { [sortColumn]: sortDirection }
          : undefined,
    };
    if (Array.isArray(filterValues.roles) && filterValues.roles.length > 0) {
      filterValues.roles.forEach((role, idx) => {
        if (role) {
          params[`role_id[${idx}]`] = role;
        }
      });
    }
    params = pickBy(params, customPredicate) as TAdminListParams;
    const response = await getAdminList(params);
    if (response?.status) {
      setAdmins(response?.data || []);
      setTotal(response?.total || 0);
    }

    setLoadingTable(false);
  };

  const handleFilterApply = (filters: IAdminFilterValues) => {
    setFilterValues({
      roles: filters.roles || [],
      status: filters.status as ECommonStatus | undefined,
    });
    setCurrentPage(1);
  };

  const handleBlockAdmin = async (admin: ISelectedAdminRowValue | null) => {
    if (!admin) return;
    setLoadingModal(true);

    const params = {
      id: admin.id.toString(),
      record_type: ERestoreEntityType.ADMIN,
      is_block: admin.status === ECommonStatus.ACTIVE,
    };
    const response = await blockUnblockRecordAPI(params);

    if (response?.status) {
      fetchAdmins();
      fetchAdminSummary();
    }

    setLoadingModal(false);
    setSelectedAdmin(null);
    setIsOpenBlockModal(false);
  };

  const handleRemoveAdmin = async () => {
    if (!selectedAdmin) return;
    setLoadingModal(true);

    const params = {
      id: selectedAdmin.id.toString(),
      record_type: ERestoreEntityType.ADMIN,
    };

    const response = await softdeleteRecord(params);
    if (response?.status) {
      fetchAdmins();
      fetchAdminSummary();
    }
    setLoadingModal(false);
    setSelectedAdmin(null);
    setIsRemoveAdminModal(false);
  };

  const isFilterApplied =
    (filterValues?.roles?.length ?? 0) > 0 || !!filterValues?.status;

  const showListUI =
    loadingTable ||
    admins.length > 0 ||
    search.length > 0 ||
    debouncedSearch.length > 0 ||
    isFilterApplied;

  return (
    <>
      {showListUI && (
        <div className="flex flex-wrap justify-between items-center gap-2.5 mb-6 pl-1">
          <div className="flex gap-2.5">
            <Input
              name="search"
              placeholder={formatText(
                'user-management.admin.search-placeholder'
              )}
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              leftIcon={{
                icon: <IconSearch size={20} className="text-neutral-500" />,
              }}
              className="max-w-77 md:min-w-77 w-full"
              inputClassName="py-[13px]"
            />
            <div className="relative">
              <Button
                icon={<IconAdjustments size={20} />}
                label={formatText('common.actions.filter')}
                variant="outline"
                className={clsx(
                  'px-4 h-12 cursor-pointer',
                  isFilterApplied && 'border-brand-default! text-brand-default!'
                )}
                onClick={() => setIsFilterModalOpen(true)}
              />
              {isFilterApplied && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
              )}
            </div>
          </div>
          <Button
            icon={<IconLayersLinked size={20} />}
            label={formatText('user-management.admin.add-admin')}
            variant="primary"
            className="px-4 h-12 cursor-pointer"
            onClick={() => setIsAddEditAdminModalOpen(true)}
          />
        </div>
      )}

      {showListUI && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {statsData?.map((stat, index) => (
            <SummaryCard
              key={index}
              variant="dashboard"
              label={formatText(stat.label)}
              value={stat?.value}
              icon={stat?.icon}
              trendDirection={stat?.trendDirection}
              isPositive={stat?.isPositive}
              trendLabel={
                stat?.trendLabel ? formatText(stat.trendLabel) : undefined
              }
              trend={stat?.trend}
              isLoading={loadingSummary}
            />
          ))}
        </div>
      )}

      <div className="mt-6">
        <CustomTable
          isLoading={loadingTable}
          dataSource={admins}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          isEmpty={
            !loadingTable &&
            admins?.length === 0 &&
            search?.length === 0 &&
            debouncedSearch?.length === 0 &&
            !isFilterApplied
          }
          fieldName={sortColumn}
          sortDirection={sortDirection}
          emptyTitle={formatText('user-management.admin.not-found-title')}
          emptyDescription={formatText('user-management.admin.not-found-desc')}
          isEmptySearch={
            admins?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          emptyButtonText={formatText('user-management.admin.add-admin')}
          emptyButtonIcon={<IconPlus size={16} />}
          onEmptyBtnClick={() => setIsAddEditAdminModalOpen(true)}
          onRowClick={(id) => {
            const admin = admins?.find((item) => item.id === id);
            if (admin) {
              setSelectedAdmin({
                id: admin.id,
                name: admin.username,
                status: admin.status,
              });
              setIsAdminDetailsOpen(true);
            }
          }}
        />
      </div>

      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <AdminFilterModal
          setIsOpen={setIsFilterModalOpen}
          onApply={handleFilterApply}
          initialValues={filterValues}
        />
      </Activity>

      <Activity mode={isAddEditAdminModalOpen ? 'visible' : 'hidden'}>
        <AddAdminModal
          setIsOpen={setIsAddEditAdminModalOpen}
          onSubmit={() => {
            fetchAdmins();
            fetchAdminSummary();
          }}
        />
      </Activity>

      <Activity mode={isAdminDetailsOpen ? 'visible' : 'hidden'}>
        <AdminDetailModal
          setIsOpen={setIsAdminDetailsOpen}
          selectedAdminId={selectedAdmin?.id}
          onRemoveSubmit={() => {
            fetchAdminSummary();
            fetchAdmins();
          }}
        />
      </Activity>

      <Activity mode={isOpenBlockModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isOpenBlockModal}
          setIsOpen={setIsOpenBlockModal}
          title="user-management.admin.block-admin-title"
          descriptionPrefix="user-management.admin.block-admin-prefix"
          descriptionSuffix="user-management.admin.block-admin-suffix"
          entityName={selectedAdmin?.name || ''}
          confirmLabel={formatText('user-management.admin.block-admin-button')}
          onConfirm={() => handleBlockAdmin(selectedAdmin)}
          isLoading={loadingModal}
          isDestructive
        />
      </Activity>

      <Activity mode={isRemoveAdminModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveAdminModal}
          setIsOpen={setIsRemoveAdminModal}
          title="user-management.admin.remove-admin-title"
          descriptionPrefix="user-management.admin.remove-admin-prefix"
          descriptionSuffix="user-management.admin.remove-admin-suffix"
          entityName={selectedAdmin?.name || ''}
          confirmLabel={formatText('user-management.admin.remove-admin-button')}
          onConfirm={handleRemoveAdmin}
          isLoading={loadingModal}
          isDestructive
        />
      </Activity>
    </>
  );
};

export default Admin;
